import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';

export const PrivacyPolicy = () => {
  return (
    <div className="pt-16" data-testid="privacy-policy-page">
      <SEO 
        title="Privacy Policy - Data Protection & User Privacy"
        description="Learn how Turcoaz Apps protects your privacy. We do not store uploaded files, respect GDPR rights, and maintain transparent data practices. Read our complete privacy policy."
        canonical="/privacy-policy"
      />

      {/* Header */}
      <section className="py-20 sm:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p 
              className="text-xs tracking-[0.2em] uppercase font-semibold text-teal-700 mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Legal
            </p>
            <h1 
              className="text-4xl sm:text-5xl tracking-tight leading-none font-semibold text-slate-900 mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Privacy Policy
            </h1>
            <p className="text-sm text-slate-500 mb-4">
              Last updated: January 2025
            </p>
            <p className="text-slate-600 leading-relaxed">
              Your privacy matters to us. This policy explains how Turcoaz Apps collects, uses, and protects your information when you use our services. We've written it in clear, straightforward language so you can understand exactly what happens with your data.
            </p>
          </div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="py-8 bg-white border-b border-slate-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm font-medium text-slate-500 mb-3">On this page</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5 text-sm">
            <span className="text-slate-600">1. Introduction</span>
            <span className="text-slate-600">7. Data Retention</span>
            <span className="text-slate-600">2. Information We Collect</span>
            <span className="text-slate-600">8. Your Privacy Rights</span>
            <span className="text-slate-600">3. How We Use Your Information</span>
            <span className="text-slate-600">9. International Data Transfers</span>
            <span className="text-slate-600">4. Cookies and Tracking</span>
            <span className="text-slate-600">10. Children's Privacy</span>
            <span className="text-slate-600">5. Third-Party Services</span>
            <span className="text-slate-600">11. Changes to This Policy</span>
            <span className="text-slate-600">6. Data Security</span>
            <span className="text-slate-600">12. Contact Information</span>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-slate max-w-none">
            
            {/* 1. Introduction */}
            <h2 
              className="text-2xl font-medium text-slate-900 mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              1. Introduction
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Turcoaz Apps ("we," "us," or "our") operates the website turcoazapps.com and related services, including Smart PDF Tools at pdf.turcoazapps.com (collectively, the "Services"). This Privacy Policy describes how we collect, use, disclose, and protect your personal information when you access or use our Services.
            </p>
            <p className="text-slate-600 leading-relaxed mb-4">
              We are committed to protecting your privacy and ensuring the security of any information you provide to us. By using our Services, you agree to the collection and use of information in accordance with this policy. If you do not agree with our practices, please do not use our Services.
            </p>
            <p className="text-slate-600 leading-relaxed mb-6">
              This Privacy Policy complies with the General Data Protection Regulation (GDPR), the California Consumer Privacy Act (CCPA), and other applicable data protection laws. We have designed our Services with privacy as a core principle, minimizing data collection and maximizing user control.
            </p>

            {/* 2. Information We Collect */}
            <h2 
              className="text-2xl font-medium text-slate-900 mb-4 mt-12"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              2. Information We Collect
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              We collect different types of information depending on how you interact with our Services. We are committed to collecting only the minimum information necessary to provide and improve our Services.
            </p>

            <h3 className="text-lg font-medium text-slate-900 mb-3 mt-6">
              2.1 Personal Data You Provide
            </h3>
            <p className="text-slate-600 leading-relaxed mb-3">
              When you voluntarily contact us or use certain features, you may provide personal information including:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 mb-4">
              <li><strong>Contact Information:</strong> Your name and email address when you submit our contact form or reach out for support.</li>
              <li><strong>Communication Content:</strong> The content of messages you send us, including feedback, questions, or support requests.</li>
              <li><strong>Account Information:</strong> If you create an account for premium features, we may collect your email address and payment information (processed securely by third-party payment providers).</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-4">
              <strong>Important:</strong> Using our core PDF tools does not require you to provide any personal information. You can merge, split, compress, and convert documents without creating an account or sharing personal data.
            </p>

            <h3 className="text-lg font-medium text-slate-900 mb-3 mt-6">
              2.2 Uploaded Files
            </h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              When you use our document processing tools, you upload files to our servers for processing. <strong>We do not store your uploaded PDF files.</strong> All uploaded documents are:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 mb-4">
              <li>Processed in isolated, secure environments</li>
              <li>Automatically and permanently deleted immediately after processing is complete</li>
              <li>Never accessed, read, analyzed, or used for any purpose other than providing the requested service</li>
              <li>Never shared with third parties or used to train AI models</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-4">
              We have no ability to recover deleted files. Once processing is complete, your documents no longer exist on our systems.
            </p>

            <h3 className="text-lg font-medium text-slate-900 mb-3 mt-6">
              2.3 Usage Data (Automatically Collected)
            </h3>
            <p className="text-slate-600 leading-relaxed mb-3">
              When you access our Services, we automatically collect certain technical information:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 mb-6">
              <li><strong>Device Information:</strong> Browser type and version, operating system, device type, and screen resolution.</li>
              <li><strong>Log Data:</strong> IP address, access times, pages viewed, referring URL, and actions taken on our site.</li>
              <li><strong>Performance Data:</strong> Page load times, error logs, and service performance metrics.</li>
              <li><strong>Aggregated Analytics:</strong> Non-personally identifiable data about how users interact with our Services as a whole.</li>
            </ul>

            {/* 3. How We Use Information */}
            <h2 
              className="text-2xl font-medium text-slate-900 mb-4 mt-12"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              3. How We Use Your Information
            </h2>
            <p className="text-slate-600 leading-relaxed mb-3">
              We use the information we collect for the following purposes:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 mb-4">
              <li><strong>Service Delivery:</strong> To provide, operate, and maintain our document processing tools and related Services.</li>
              <li><strong>Communication:</strong> To respond to your inquiries, provide customer support, and send service-related notices.</li>
              <li><strong>Improvement:</strong> To understand how users interact with our Services, identify issues, and make improvements.</li>
              <li><strong>Security:</strong> To detect, prevent, and address technical issues, fraud, and security vulnerabilities.</li>
              <li><strong>Legal Compliance:</strong> To comply with applicable laws, regulations, and legal processes.</li>
              <li><strong>Advertising:</strong> To display relevant advertisements through third-party advertising networks (see Section 5).</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-6">
              We do not sell your personal information to third parties. We do not use your uploaded documents for any purpose other than providing the specific service you requested.
            </p>

            {/* 4. Cookies and Tracking Technologies */}
            <h2 
              className="text-2xl font-medium text-slate-900 mb-4 mt-12"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              4. Cookies and Tracking Technologies
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              We use cookies and similar tracking technologies to enhance your experience, analyze usage patterns, and deliver personalized advertising. Cookies are small text files stored on your device that help us recognize you and remember your preferences.
            </p>

            <h3 className="text-lg font-medium text-slate-900 mb-3 mt-6">
              4.1 Types of Cookies We Use
            </h3>
            <ul className="list-disc list-inside text-slate-600 space-y-2 mb-4">
              <li><strong>Essential Cookies:</strong> Required for the website to function properly. These cannot be disabled without affecting site functionality.</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how visitors interact with our website by collecting anonymous statistical information.</li>
              <li><strong>Advertising Cookies:</strong> Used by advertising partners (including Google AdSense) to deliver relevant advertisements based on your interests and browsing behavior.</li>
              <li><strong>Preference Cookies:</strong> Remember your settings and preferences for a more personalized experience.</li>
            </ul>

            <h3 className="text-lg font-medium text-slate-900 mb-3 mt-6">
              4.2 Google AdSense and Advertising
            </h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              Our website uses Google AdSense to display advertisements. Google AdSense uses cookies to serve ads based on your prior visits to our website and other websites on the Internet. Google's use of advertising cookies enables it and its partners to serve ads based on your browsing patterns.
            </p>
            <p className="text-slate-600 leading-relaxed mb-4">
              You may opt out of personalized advertising by visiting <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-700">Google Ads Settings</a>. Alternatively, you can opt out of third-party vendor cookies by visiting the <a href="https://optout.networkadvertising.org/" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-700">Network Advertising Initiative opt-out page</a>.
            </p>

            <h3 className="text-lg font-medium text-slate-900 mb-3 mt-6">
              4.3 Analytics Services
            </h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              We use analytics services to collect and analyze information about how users interact with our Services. These services may collect information such as how often you visit, which pages you view, and what other sites you visited before coming to our website. We use this information solely to improve our Services.
            </p>

            <h3 className="text-lg font-medium text-slate-900 mb-3 mt-6">
              4.4 Managing Cookies
            </h3>
            <p className="text-slate-600 leading-relaxed mb-6">
              Most web browsers allow you to control cookies through their settings. You can typically choose to block all cookies, accept all cookies, or receive a notification when a cookie is set. Please note that blocking certain cookies may impact the functionality of our Services. For more information, see our <Link to="/cookie-policy" className="text-teal-600 hover:text-teal-700">Cookie Policy</Link>.
            </p>

            {/* 5. Third-Party Services */}
            <h2 
              className="text-2xl font-medium text-slate-900 mb-4 mt-12"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              5. Third-Party Services
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              We work with trusted third-party service providers to operate and improve our Services. These providers have access to your information only to perform specific tasks on our behalf and are obligated to protect your data.
            </p>

            <h3 className="text-lg font-medium text-slate-900 mb-3 mt-6">
              5.1 Advertising Partners
            </h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              We use Google AdSense and potentially other advertising networks to display advertisements on our website. These partners may use cookies and similar technologies to collect information about your browsing activities across different websites to provide targeted advertising. This information does not include your name or contact details.
            </p>

            <h3 className="text-lg font-medium text-slate-900 mb-3 mt-6">
              5.2 AI and Document Processing Providers
            </h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              Some of our advanced features may utilize artificial intelligence technologies provided by third-party services. When processing your documents with AI features:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 mb-4">
              <li>Your document content is transmitted securely to the AI provider solely for processing</li>
              <li>Data is not retained by AI providers beyond the processing session</li>
              <li>Your documents are not used to train or improve AI models</li>
              <li>All transmissions are encrypted using industry-standard protocols</li>
            </ul>

            <h3 className="text-lg font-medium text-slate-900 mb-3 mt-6">
              5.3 Infrastructure and Hosting
            </h3>
            <p className="text-slate-600 leading-relaxed mb-6">
              Our Services are hosted on secure cloud infrastructure. Our hosting providers maintain strict security standards and comply with relevant data protection regulations. We select providers that offer strong data protection guarantees and maintain data centers in jurisdictions with robust privacy laws.
            </p>

            {/* 6. Data Security */}
            <h2 
              className="text-2xl font-medium text-slate-900 mb-4 mt-12"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              6. Data Security
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              We implement comprehensive security measures to protect your information from unauthorized access, alteration, disclosure, or destruction. Our security practices include:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 mb-4">
              <li><strong>Encryption:</strong> All data transmitted between your device and our servers is encrypted using TLS (Transport Layer Security).</li>
              <li><strong>Secure Processing:</strong> File processing occurs in isolated, ephemeral environments that are destroyed after each operation.</li>
              <li><strong>Access Controls:</strong> Strict access controls limit who can access our systems and data.</li>
              <li><strong>Regular Audits:</strong> We conduct regular security assessments and vulnerability testing.</li>
              <li><strong>Incident Response:</strong> We maintain procedures for detecting, responding to, and recovering from security incidents.</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-6">
              While we strive to protect your information using commercially reasonable measures, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security but are committed to promptly addressing any security issues that arise.
            </p>

            {/* 7. Data Retention */}
            <h2 
              className="text-2xl font-medium text-slate-900 mb-4 mt-12"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              7. Data Retention
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              We retain different types of data for different periods based on the nature of the information and our legitimate business needs:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 mb-4">
              <li><strong>Uploaded Files:</strong> <strong>Not retained.</strong> All uploaded documents are automatically and permanently deleted immediately after processing. We maintain no copies, backups, or archives of your files.</li>
              <li><strong>Contact Information:</strong> Retained for as long as necessary to respond to your inquiry and for a reasonable period thereafter for follow-up purposes, unless you request deletion.</li>
              <li><strong>Usage Data:</strong> Aggregated and anonymized analytics data may be retained indefinitely for statistical purposes. Identifiable log data is typically retained for up to 90 days.</li>
              <li><strong>Account Data:</strong> If you create an account, your information is retained until you delete your account or request deletion.</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-6">
              Upon expiration of retention periods or upon your valid deletion request, we will delete or anonymize your personal information in accordance with our data deletion procedures.
            </p>

            {/* 8. User Rights (GDPR) */}
            <h2 
              className="text-2xl font-medium text-slate-900 mb-4 mt-12"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              8. Your Privacy Rights
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Depending on your location, you may have certain rights regarding your personal information under applicable data protection laws, including the GDPR and CCPA. These rights may include:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 mb-4">
              <li><strong>Right to Access:</strong> You have the right to request a copy of the personal information we hold about you.</li>
              <li><strong>Right to Rectification:</strong> You have the right to request correction of inaccurate or incomplete personal information.</li>
              <li><strong>Right to Erasure:</strong> You have the right to request deletion of your personal information, subject to certain exceptions.</li>
              <li><strong>Right to Restrict Processing:</strong> You have the right to request that we limit how we use your personal information.</li>
              <li><strong>Right to Data Portability:</strong> You have the right to receive your personal information in a structured, commonly used, machine-readable format.</li>
              <li><strong>Right to Object:</strong> You have the right to object to processing of your personal information for certain purposes, including direct marketing.</li>
              <li><strong>Right to Withdraw Consent:</strong> Where processing is based on consent, you have the right to withdraw that consent at any time.</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-4">
              To exercise any of these rights, please contact us using the information provided in Section 12. We will respond to your request within the timeframe required by applicable law (typically within 30 days). We may need to verify your identity before processing your request.
            </p>
            <p className="text-slate-600 leading-relaxed mb-6">
              If you believe we have not adequately addressed your concerns, you have the right to lodge a complaint with your local data protection supervisory authority.
            </p>

            {/* 9. International Data Transfers */}
            <h2 
              className="text-2xl font-medium text-slate-900 mb-4 mt-12"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              9. International Data Transfers
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Our Services are available globally, and your information may be processed in countries other than the country in which you reside. These countries may have data protection laws that are different from the laws of your country.
            </p>
            <p className="text-slate-600 leading-relaxed mb-4">
              When we transfer personal data outside the European Economic Area (EEA), we ensure that adequate safeguards are in place to protect your information, including:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 mb-4">
              <li>Transfers to countries recognized by the European Commission as providing an adequate level of data protection</li>
              <li>Use of Standard Contractual Clauses (SCCs) approved by the European Commission</li>
              <li>Other appropriate safeguards as required by applicable data protection law</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-6">
              Regardless of where your data is processed, we apply the same protections described in this Privacy Policy and comply with applicable data protection requirements.
            </p>

            {/* 10. Children's Information */}
            <h2 
              className="text-2xl font-medium text-slate-900 mb-4 mt-12"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              10. Children's Privacy
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Our Services are not directed to children under the age of 16. We do not knowingly collect personal information from children under 16. If you are a parent or guardian and believe that your child has provided us with personal information, please contact us immediately.
            </p>
            <p className="text-slate-600 leading-relaxed mb-6">
              If we become aware that we have collected personal information from a child under 16 without verification of parental consent, we will take steps to delete that information from our servers promptly.
            </p>

            {/* 11. Changes to This Policy */}
            <h2 
              className="text-2xl font-medium text-slate-900 mb-4 mt-12"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              11. Changes to This Privacy Policy
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              We may update this Privacy Policy from time to time to reflect changes in our practices, technologies, legal requirements, or other factors. When we make changes, we will:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 mb-4">
              <li>Update the "Last updated" date at the top of this policy</li>
              <li>Post the revised policy on this page</li>
              <li>For material changes, provide notice through our website or, where appropriate, by email</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-6">
              We encourage you to review this Privacy Policy periodically to stay informed about how we protect your information. Your continued use of our Services after any changes indicates your acceptance of the updated policy.
            </p>

            {/* 12. Contact Information */}
            <h2 
              className="text-2xl font-medium text-slate-900 mb-4 mt-12"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              12. Contact Information
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="bg-slate-50 rounded-xl p-6 mb-6">
              <p className="text-slate-600 mb-2">
                <strong>Email:</strong>{' '}
                <a href="mailto:contact@turcoazapps.com" className="text-teal-600 hover:text-teal-700">
                  contact@turcoazapps.com
                </a>
              </p>
              <p className="text-slate-600 mb-2">
                <strong>Contact Form:</strong>{' '}
                <Link to="/contact" className="text-teal-600 hover:text-teal-700">
                  turcoazapps.com/contact
                </Link>
              </p>
              <p className="text-slate-600">
                <strong>Website:</strong>{' '}
                <a href="https://turcoazapps.com" className="text-teal-600 hover:text-teal-700">
                  turcoazapps.com
                </a>
              </p>
            </div>
            <p className="text-slate-600 leading-relaxed mb-4">
              For data protection inquiries or to exercise your privacy rights, please include "Privacy Request" in your subject line to ensure prompt handling of your request. We will acknowledge receipt of your request within 5 business days and provide a substantive response within 30 days, as required by applicable law.
            </p>
            <p className="text-slate-600 leading-relaxed">
              We are committed to working with you to resolve any concerns about your privacy and will respond to all legitimate requests in a timely manner.
            </p>

          </div>
        </div>
      </section>
    </div>
  );
};
